/**
 * Download ZIP produzione VPS.
 * Endpoint autenticato: GET /api/download/vps-zip
 * Endpoint statico (fallback): /downloads/ethos-gestionale-vps.zip
 */
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, Package, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "@/api";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function fmtSize(n) {
  if (!n) return "—";
  if (n > 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024).toFixed(0)} KB`;
}

function fmtDate(unix) {
  if (!unix) return "—";
  return new Date(unix * 1000).toLocaleString("it-IT");
}

export default function ZipDownloadPanel() {
  const { data: info } = useQuery({
    queryKey: ["vps-zip-info"],
    queryFn: () => api.get("/download/vps-zip/info").then((r) => r.data),
  });

  const downloadAuthenticated = async () => {
    try {
      const res = await api.get("/download/vps-zip", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ethos-gestionale-vps.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Download avviato");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download fallito");
    }
  };

  const staticUrl = `${REACT_APP_BACKEND_URL}/downloads/ethos-gestionale-vps.zip`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(staticUrl);
      toast.success("Link copiato negli appunti");
    } catch {
      toast.error("Copia fallita — copia manualmente il link");
    }
  };

  return (
    <section
      className="bg-white border border-slate-200 rounded-md p-5"
      data-testid="zip-download-panel"
    >
      <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-2">
        <Package className="w-4 h-4" /> Pacchetto produzione VPS
      </div>

      <p className="text-[12px] text-slate-600 mb-4 max-w-2xl">
        Scarica il pacchetto completo con Docker Compose, nginx, backend, frontend
        e script di installazione — pronto per essere caricato sul tuo VPS Aruba.
      </p>

      <div className="grid md:grid-cols-3 gap-3 mb-4 text-xs">
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Dimensione</div>
          <div className="font-mono font-bold text-lg">{fmtSize(info?.size)}</div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Ultima generazione</div>
          <div className="font-mono">{fmtDate(info?.mtime)}</div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Stato</div>
          <div className={`font-bold ${info?.available ? "text-emerald-700" : "text-rose-700"}`}>
            {info?.available ? "Disponibile" : "Non ancora generato"}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={downloadAuthenticated}
          disabled={!info?.available}
          data-testid="btn-download-zip"
          className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white text-sm font-semibold rounded-sm shadow-brutal"
        >
          <Download className="w-4 h-4" /> Scarica ZIP
        </button>
        <a
          href={staticUrl}
          target="_blank" rel="noreferrer"
          data-testid="btn-direct-link"
          className="inline-flex items-center gap-2 px-3 py-2 border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold rounded-sm"
        >
          <ExternalLink className="w-4 h-4" /> Link diretto
        </a>
        <button
          onClick={copyLink}
          data-testid="btn-copy-link"
          className="inline-flex items-center gap-2 px-3 py-2 border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold rounded-sm"
        >
          <Copy className="w-4 h-4" /> Copia link
        </button>
      </div>

      <div className="mt-3 text-[11px] text-slate-500 font-mono break-all bg-slate-50 border border-slate-200 rounded-sm p-2">
        {staticUrl}
      </div>
    </section>
  );
}
